import { ProductAttributeOption, ProductAttributes } from '../types';

export interface NormalizedOption {
  name: string;
  extra_price: number;
  is_out_of_stock?: boolean;
}

export const COLOR_MAP: Record<string, string> = {
  // Base & Common
  black: '#111827',
  white: '#ffffff',
  red: '#ef4444',
  blue: '#2563eb',
  green: '#10b981',
  yellow: '#f59e0b',
  orange: '#f97316',
  purple: '#8b5cf6',
  pink: '#ec4899',
  gray: '#6b7280',
  grey: '#6b7280',

  // Fashion & Attire Shades
  'light ash': '#e2e8f0',
  ash: '#cbd5e1',
  'dark ash': '#64748b',
  'light coffee': '#a27b5c',
  coffee: '#6f4e37',
  'dark coffee': '#4a2c1b',
  'sand beige': '#e6cca0',
  beige: '#d2b48c',
  bringe: '#d2b48c', // common typo for beige
  'off white': '#f8fafc',
  'off-white': '#f8fafc',
  cream: '#fef3c7',
  ivory: '#fffff0',
  navy: '#1e3a8a',
  'navy blue': '#1e3a8a',
  'royal blue': '#1d4ed8',
  'sky blue': '#38bdf8',
  maroon: '#800000',
  olive: '#65a30d',
  'olive green': '#4d7c0f',
  khaki: '#c3b091',
  charcoal: '#374151',
  teal: '#0d9488',
  mustard: '#d97706',
  rust: '#b45309',
  camel: '#c19a6b',
  chocolate: '#7b3f00',
  tan: '#d2b48c',
  peach: '#fed7aa',
  mint: '#a7f3d0',
  lavender: '#e9d5ff',
  burgundy: '#831843',
  silver: '#cbd5e1',
  gold: '#d4af37',

  // Bengali Color Names
  কালো: '#111827',
  সাদা: '#ffffff',
  'লাইট অ্যাশ': '#e2e8f0',
  অ্যাশ: '#cbd5e1',
  'লাইট কফি': '#a27b5c',
  কফি: '#6f4e37',
  'স্যান্ড বেইজ': '#e6cca0',
  বেইজ: '#d2b48c',
  'অফ হোয়াইট': '#f8fafc',
  ক্রিম: '#fef3c7',
  লাল: '#ef4444',
  নীল: '#2563eb',
  'নেভি ব্লু': '#1e3a8a',
  'আকাশি নীল': '#38bdf8',
  সবুজ: '#10b981',
  জলপাই: '#65a30d',
  হলুদ: '#f59e0b',
  কমলা: '#f97316',
  মেরুন: '#800000',
  গোলাপি: '#ec4899',
  বেগুনী: '#8b5cf6',
  ছাই: '#cbd5e1',
  বাদামী: '#8b4513',
};

/**
 * Returns a hex color for a given color name or code.
 */
export const getColorHex = (name: string): string => {
  if (!name) return '#94a3b8';
  const trimmed = name.trim();
  if (trimmed.startsWith('#')) return trimmed;

  const lower = trimmed.toLowerCase();
  if (COLOR_MAP[lower]) return COLOR_MAP[lower];

  // Try substring matching
  const matchedKey = Object.keys(COLOR_MAP).find(k => lower.includes(k));
  if (matchedKey) return COLOR_MAP[matchedKey];

  return '#94a3b8';
};

/**
 * Checks if a hex color is light (needs a border or dark outline).
 */
export const isLightColor = (hex: string): boolean => {
  if (!hex || hex === '#ffffff' || hex === '#fff' || hex === '#f8fafc' || hex === '#fafaf9' || hex === '#fffff0' || hex === '#e2e8f0' || hex === '#fef3c7') {
    return true;
  }
  if (hex.startsWith('#') && (hex.length === 7 || hex.length === 4)) {
    let r = 255, g = 255, b = 255;
    if (hex.length === 7) {
      r = parseInt(hex.substring(1, 3), 16) || 255;
      g = parseInt(hex.substring(3, 5), 16) || 255;
      b = parseInt(hex.substring(5, 7), 16) || 255;
    }
    const lum = (r * 299 + g * 587 + b * 114) / 1000;
    return lum > 190;
  }
  return false;
};

/**
 * Normalizes an attribute option into { name, extra_price, is_out_of_stock }
 */
export const normalizeOption = (opt: string | ProductAttributeOption): NormalizedOption => {
  if (!opt) return { name: '', extra_price: 0, is_out_of_stock: false };
  if (typeof opt === 'string') {
    const raw = opt.trim();
    const isOos = /\b(out of stock|stock out|স্টক শেষ|oos|sold out)\b/i.test(raw);
    const cleanName = raw.replace(/\s*\((out of stock|stock out|স্টক শেষ|oos|sold out)\)\s*/gi, '').trim();
    return { name: cleanName || raw, extra_price: 0, is_out_of_stock: isOos };
  }

  const rawName = (opt.name || '').trim();
  const isOos =
    (opt as any).is_out_of_stock === true ||
    (opt as any).out_of_stock === true ||
    (opt as any).in_stock === false ||
    (opt as any).is_available === false ||
    ((opt as any).stock !== undefined && Number((opt as any).stock) <= 0) ||
    /\b(out of stock|stock out|স্টক শেষ|oos|sold out)\b/i.test(rawName);

  const cleanName = rawName.replace(/\s*\((out of stock|stock out|স্টক শেষ|oos|sold out)\)\s*/gi, '').trim();

  return {
    name: cleanName || rawName,
    extra_price: Number(opt.extra_price ?? opt.price ?? 0),
    is_out_of_stock: isOos,
  };
};

/**
 * Normalizes an array of options into an array of NormalizedOption.
 * Handles comma-separated options gracefully.
 */
export const normalizeOptions = (options?: (string | ProductAttributeOption)[]): NormalizedOption[] => {
  if (!Array.isArray(options)) return [];

  const result: NormalizedOption[] = [];

  for (const item of options) {
    if (!item) continue;
    if (typeof item === 'string') {
      const trimmed = item.trim();
      // If single string containing commas (e.g., 'Black, Light Ash, Light Coffee')
      if (trimmed.includes(',')) {
        const parts = trimmed.split(',').map(p => p.trim()).filter(Boolean);
        for (const part of parts) {
          result.push(normalizeOption(part));
        }
      } else if (trimmed.includes(' ') && !trimmed.includes('|') && /^(s|m|l|xl|xxl|2xl|3xl|4xl)(\s+(s|m|l|xl|xxl|2xl|3xl|4xl))+$/i.test(trimmed)) {
        // Space-separated simple sizes like "S M L XL XXL"
        const parts = trimmed.split(/\s+/).filter(Boolean);
        for (const part of parts) {
          result.push(normalizeOption(part));
        }
      } else {
        result.push(normalizeOption(trimmed));
      }
    } else {
      result.push(normalizeOption(item));
    }
  }

  return result.filter(o => o.name !== '');
};

/**
 * Formats extra price display (e.g. "+৳৩০০" or "+৳300")
 */
export const formatExtraPriceBadge = (extraPrice: number, _lang: string = 'bn'): string => {
  if (!extraPrice || extraPrice <= 0) return '';
  const formatted = extraPrice.toLocaleString();
  return ` (+৳${formatted})`;
};

/**
 * Calculates final unit price based on base price and all selected attribute options
 */
export const calculateProductPriceWithAttributes = (
  basePrice: number,
  attributes?: ProductAttributes,
  selectedColor?: string,
  selectedSize?: string,
  selectedWeight?: string,
  selectedCustom?: Record<string, string>
): number => {
  let total = Number(basePrice || 0);
  if (!attributes) return total;

  // Colors
  if (selectedColor && attributes.colors?.enabled) {
    const opts = normalizeOptions(attributes.colors.options);
    const found = opts.find(o => o.name.toLowerCase() === selectedColor.toLowerCase());
    if (found) total += found.extra_price;
  }

  // Sizes
  if (selectedSize && attributes.sizes?.enabled) {
    const opts = normalizeOptions(attributes.sizes.options);
    const found = opts.find(o => o.name.toLowerCase() === selectedSize.toLowerCase());
    if (found) total += found.extra_price;
  }

  // Weights
  if (selectedWeight && attributes.weights?.enabled) {
    const opts = normalizeOptions(attributes.weights.options);
    const found = opts.find(o => o.name.toLowerCase() === selectedWeight.toLowerCase());
    if (found) total += found.extra_price;
  }

  // Custom attributes (e.g. Hijab, Material, Warranty)
  if (selectedCustom && attributes.custom && Array.isArray(attributes.custom)) {
    for (const group of attributes.custom) {
      if (!group.enabled) continue;
      const groupKey = group.id || group.name;
      const selectedVal = selectedCustom[groupKey] || selectedCustom[group.name];
      if (selectedVal) {
        const opts = normalizeOptions(group.options);
        const found = opts.find(o => o.name.toLowerCase() === selectedVal.toLowerCase());
        if (found) total += found.extra_price;
      }
    }
  }

  return total;
};

